import { useEffect, useMemo, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const methodClasses = {
  get: 'bg-blue-100 text-blue-700',
  post: 'bg-green-100 text-green-700',
  put: 'bg-amber-100 text-amber-700',
  patch: 'bg-purple-100 text-purple-700',
  delete: 'bg-red-100 text-red-700',
};

const categoryForPath = (path) => {
  if (path.includes('/external-mails') || path.includes('/external/mail-configs')) return 'External Apps';
  if (path.includes('/auth')) return 'Authentication';
  if (path.includes('/companies') || path.includes('/admin/company')) return 'Companies';
  if (path.includes('/users')) return 'Users';
  if (path.includes('/mail-configs')) return 'Mail Configurations';
  if (path.includes('/batches') || path.includes('/files') || path.includes('/crossfiles')) return 'Files and Batches';
  if (path.includes('/send-batches')) return 'Sending';
  if (path.includes('/dashboard')) return 'Dashboard';
  if (path.includes('/admin/audit-logs')) return 'Audit Logs';
  if (path.includes('/admin/api-docs')) return 'API Docs';
  return 'Other';
};

const detailForPath = (method, path) => {
  if (path === '/api/external-mails/send') return 'External app JSON send endpoint. Requires X-API-Key, a mail_config_guid from Mail Configurations, recipients, subject, body_html, and optional save_copy_on_sent. The API key user must allow external API access and the caller source must match that user source filter. Optional attachment_base64 is checked against the company per-file size limit.';
  if (path === '/api/external-mails/send-with-attachment') return 'External app multipart send endpoint for a shared attachment. Requires X-API-Key and form fields mail_config_guid, recipients, subject, body_html, save_copy_on_sent, and attachment. The API key user source filter is enforced and the attachment is rejected if it exceeds the company per-file limit.';
  if (path.includes('/api/external/mail-configs') && method === 'post') return 'External admin-only endpoint to create a mail configuration for the API key user company. SuperAdmin keys may pass company_id; CompanyAdmin keys are scoped to their own company.';
  if (path.includes('/api/external/mail-configs') && method === 'put') return 'External admin-only endpoint to replace an existing mail configuration by GUID. Empty secret fields keep the current stored secret.';
  if (path.includes('/azure-secret')) return 'External admin-only endpoint to rotate the Azure client secret by mail configuration GUID and optionally update its expiry reminder fields.';
  if (path.includes('/api/admin/audit-logs')) return 'SuperAdmin audit feed for logins, external API access, sends, creates, deletes, errors, IP address, forwarded IP, user agent, and related details.';
  if (path.includes('/api/companies')) return 'Company administration endpoint. Company IDs are GUIDs and each company has total storage and per-file upload limits.';
  if (path.includes('/api/mail-configs')) return 'Frontend-authenticated mail configuration management for SMTP, Azure Graph API, and SendGrid providers.';
  if (path.includes('/api/batches/upload')) return 'Upload one file or a ZIP. ZIP contents are extracted and each internal file is checked against the company per-file limit while the total extracted bytes count toward company storage.';
  return '';
};

const exampleForMethod = (method, path) => {
  if (path === '/api/auth/login') return { email: 'admin@example.com', password: 'StrongPass123' };
  if (path === '/api/external-mails/send') return {
    mail_config_guid: '6f07a146-b4f1-4e35-8e92-7b7084f7fd18',
    recipients: ['person@example.com'],
    subject: 'Hello from BatchMailer',
    body_html: '<p>Hello</p>',
    save_copy_on_sent: false,
    attachment_filename: 'optional.pdf',
    attachment_base64: 'base64-encoded-file-content'
  };
  if (path === '/api/external-mails/send-with-attachment') return {
    headers: { 'X-API-Key': 'bm_your_key' },
    form: {
      mail_config_guid: '6f07a146-b4f1-4e35-8e92-7b7084f7fd18',
      recipients: '["person@example.com","other@example.com"]',
      subject: 'Hello from BatchMailer',
      body_html: '<p>Hello</p>',
      save_copy_on_sent: false,
      attachment: 'binary file upload'
    }
  };
  if (path.includes('/api/external/mail-configs') && method === 'post') return {
    name: 'Main SMTP',
    provider: 'smtp',
    from_email: 'sender@example.com',
    from_name: 'Sender',
    smtp_host: 'smtp.example.com',
    smtp_port: 587,
    use_tls: true
  };
  if (path.includes('/azure-secret')) return {
    client_secret: 'new-secret-value',
    client_secret_expire: '2026-12-31',
    secret_expiry_reminder_email: 'ops@example.com',
    secret_expiry_reminder_days: 5
  };
  if (path === '/api/send-batches') return {
    source_type: 'manual',
    mail_config_id: 'mail-config-id',
    recipients: ['person@example.com', 'other@example.com'],
    mode: 'link',
    subject: 'Campaign subject',
    body_html: '<p>Hello {recipient_email}</p>',
    save_copy_on_sent: false,
  };
  if (path === '/api/companies' && method === 'post') return { name: 'Acme Inc', storage_limit_bytes: 1073741824, max_file_size_bytes: 26214400 };
  if (path.includes('/api/users') && ['post', 'patch'].includes(method)) return {
    company_id: 'company-id',
    email: 'user@example.com',
    password: 'StrongPass123',
    role: 'companyuser',
    allow_external_api: false,
    external_api_allowed_hosts: ['203.0.113.10', '10.0.0.0/24'],
  };
  if (path.includes('/api/mail-configs') && ['post', 'put'].includes(method)) return {
    company_id: 'company-id',
    name: 'Main SMTP',
    provider: 'smtp',
    from_email: 'sender@example.com',
    from_name: 'Sender',
    smtp_host: 'smtp.example.com',
    smtp_port: 587,
    use_tls: true,
  };
  return null;
};

const authForPath = (path) => (
  path.includes('/api/external-mails') || path.includes('/api/external/mail-configs')
    ? 'External only: X-API-Key header'
    : path.startsWith('/api/auth/login')
      ? 'Public'
      : 'Frontend JWT bearer token'
);

const schemaNameFromRef = (ref) => ref?.split('/').pop();

export default function ApiDocs() {
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/admin/api-docs')
      .then((res) => {
        const rows = [];
        Object.entries(res.data.paths || {}).forEach(([path, methods]) => {
          Object.entries(methods).forEach(([method, spec]) => {
            const requestContent = spec.requestBody?.content?.['application/json']?.schema;
            rows.push({
              id: `${method}-${path}`,
              method,
              path,
              category: categoryForPath(path),
              summary: spec.summary || spec.operationId || 'API method',
              description: spec.description || '',
              detail: detailForPath(method, path),
              auth: authForPath(path),
              requestSchema: schemaNameFromRef(requestContent?.$ref) || requestContent?.type || null,
              responseCodes: Object.keys(spec.responses || {}),
              example: exampleForMethod(method, path),
              tags: spec.tags || [],
            });
          });
        });
        setDocs(rows.sort((a, b) => `${a.path}${a.method}`.localeCompare(`${b.path}${b.method}`)));
      })
      .catch(() => toast.error('Failed to load API methods'));
  }, []);

  const filteredDocs = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return docs;
    return docs.filter((item) => (
      item.path.toLowerCase().includes(term) ||
      item.method.toLowerCase().includes(term) ||
      item.summary.toLowerCase().includes(term) ||
      item.tags.join(' ').toLowerCase().includes(term)
    ));
  }, [docs, search]);

  const groupedDocs = useMemo(() => (
    filteredDocs.reduce((groups, item) => {
      groups[item.category] = groups[item.category] || [];
      groups[item.category].push(item);
      return groups;
    }, {})
  ), [filteredDocs]);

  return (
    <div className="space-y-6" data-testid="api-docs-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>API Docs</h1>
          <p className="text-white/80 mt-1">Super admin view of backend methods for frontend and external integrations.</p>
        </div>
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search methods..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white"
            data-testid="api-docs-search"
          />
        </div>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            Methods ({filteredDocs.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(groupedDocs).map(([category, items]) => (
            <div key={category} className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">{category}</h3>
              {items.map((item) => (
                <div key={item.id} className="rounded-lg border bg-white p-4" data-testid={`api-method-${item.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge className={methodClasses[item.method] || 'bg-gray-100 text-gray-700'}>
                          {item.method.toUpperCase()}
                        </Badge>
                        <code className="text-sm font-semibold text-gray-900">{item.path}</code>
                      </div>
                      <div className="font-medium text-gray-800">{item.summary}</div>
                      {(item.description || item.detail) && <p className="text-sm text-gray-600">{item.description || item.detail}</p>}
                      {item.description && item.detail && <p className="text-sm text-gray-600">{item.detail}</p>}
                      <div className="flex flex-wrap gap-2 pt-1 text-xs">
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">{item.auth}</span>
                        {item.requestSchema && <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">Body: {item.requestSchema}</span>}
                        {item.responseCodes.length > 0 && <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-700">Responses: {item.responseCodes.join(', ')}</span>}
                      </div>
                      {item.example && (
                        <pre className="mt-3 overflow-x-auto rounded-md bg-gray-900 p-3 text-xs text-gray-100">
                          {JSON.stringify(item.example, null, 2)}
                        </pre>
                      )}
                    </div>
                    {item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {item.tags.map((tag) => (
                          <span key={tag} className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
